// src/services/TradingMetricsEngine.ts
/**
 * 🚀 TradingMetricsEngine v1.0
 *
 * Motore centralizzato per calcolo metriche trading
 * - Lazy evaluation (calcola solo ciò che serve)
 * - Memoization con cache LRU
 * - Filtri temporali e multi-criterio
 * - API fluente type-safe
 * - Performance monitoring integrato
 *
 * @author TradingSpace
 * @version 1.0.0
 */

import { StandardTrade, TradingAccount } from '../types/database';
import { PerformanceMonitor } from './PerformanceMonitor';
import { resolveTradePnl, toFiniteTradeNumber } from '../utils/tradePnl';

// ==================== HELPERS ====================

/**
 * Normalize a trade so all numeric fields used by the metrics engine are
 * actual numbers, not strings. Preserves the rest of the object as-is.
 *
 * Why this exists: some trades arrive from CSV or Supabase JSONB with
 * `profit`, `commission`, `swap`, etc. as strings. JS `sum + "16.7"`
 * concatenates instead of adding → bestTrade / worstTrade still work
 * (Math.max coerces) but `reduce(sum + t.profit, 0)` produces a string.
 * Then `round(string, 2)` returns 0 because `string * 10 = NaN`.
 */
const normalizeTradeNumerics = (trade: StandardTrade): StandardTrade => {
  const canonicalPnl = resolveTradePnl(trade);
  return {
    ...trade,
    // The engine performs almost every calculation on `profit`.
    // Normalize it to canonical net P&L so dashboard and report stay aligned.
    profit: canonicalPnl,
    netProfit: canonicalPnl,
    netPnl: canonicalPnl,
    commission: trade.commission !== undefined ? toFiniteTradeNumber(trade.commission) : trade.commission,
    swap: (trade as any).swap !== undefined ? toFiniteTradeNumber((trade as any).swap) : (trade as any).swap,
    quantity: trade.quantity !== undefined ? toFiniteTradeNumber(trade.quantity) : trade.quantity,
    entryPrice: trade.entryPrice !== undefined ? toFiniteTradeNumber(trade.entryPrice) : trade.entryPrice,
    exitPrice: trade.exitPrice !== undefined ? toFiniteTradeNumber(trade.exitPrice) : trade.exitPrice,
    stopLoss: trade.stopLoss !== undefined ? toFiniteTradeNumber(trade.stopLoss) : trade.stopLoss,
    takeProfit: trade.takeProfit !== undefined ? toFiniteTradeNumber(trade.takeProfit) : trade.takeProfit,
  } as StandardTrade;
};

// ==================== TYPES ====================

/**
 * Opzioni di filtro per i calcoli
 */
export interface MetricsFilterOptions {
  startDate?: Date;
  endDate?: Date;
  symbols?: string[];
  directions?: ('BUY' | 'SELL' | 'LONG' | 'SHORT')[];
  minProfit?: number;
  maxProfit?: number;
}

/**
 * Performance metrics (base)
 */
export interface PerformanceMetrics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  lossRate: number;
  totalNetPnl: number;
  grossProfit: number;
  grossLoss: number;
  averageTrade: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  bestTrade: number;
  worstTrade: number;
  totalReturn?: number;
  cagr?: number;
}

/**
 * Risk metrics
 */
export interface RiskMetrics {
  maxDrawdown: {
    absolute: number;
    percentage: number;
    duration: number;
  };
  sharpeRatio: number;
  sortinoRatio: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
  standardDeviation: number;
  volatility: number;
  recoveryFactor?: number;
  calmarRatio?: number;
  maxDrawdownPercent?: number;
}

/**
 * Breakdown metrics
 */
export interface BreakdownMetrics {
  byAsset: Array<{
    symbol: string;
    trades: number;
    profit: number;
    winRate: number;
    avgTrade: number;
  }>;
  byDirection: {
    long: DirectionStats;
    short: DirectionStats;
  };
  byHourOfDay: { [hour: string]: TimeSlotStats };
  byDayOfWeek: { [day: string]: TimeSlotStats };
}

interface DirectionStats {
  trades: number;
  profit: number;
  winRate: number;
  avgTrade: number;
}

interface TimeSlotStats {
  trades: number;
  pnl: number;
  winRate: number;
}

/**
 * Advanced metrics
 */
export interface AdvancedMetrics {
  expectancy: number;
  kellyCriterion: number;
  sqn: number;
  gainToPainRatio: number;
  profitabilityIndex: number;
  recoveryFactor: number;
}

/**
 * Duration analysis
 */
export interface DurationMetrics {
  averageWinDuration: number;
  averageLossDuration: number;
  maxDuration: number;
  minDuration: number;
}

/**
 * Metriche emotive e psicologiche
 */
export interface EmotionalMetrics {
  overtradingScore: number; // 0-100 (0=ottimo, 100=grave)
  revengeTradesCount: number;
  revengeTradesPnl: number;
  emotionalTradingIndex: number; // 0-10
  averageTradesPerDay: number;
  tradingFrequencySpikes: number;
  impulsiveTradePercentage: number;
}

/**
 * Risk metrics avanzati
 */
export interface AdvancedRiskMetrics {
  calmarRatio: number;
  valueAtRisk95: number; // VaR al 95%
  valueAtRisk99: number; // VaR al 99%
  conditionalVaR95: number; // CVaR al 95%
  conditionalVaR99: number; // CVaR al 99%
  ulcerIndex: number;
  maxTimeToRecover: number; // giorni
}

/**
 * Performance per sessione di trading
 */
export interface SessionMetrics {
  asian: SessionStats;
  european: SessionStats;
  american: SessionStats;
  overlap: {
    europeAsia: SessionStats;
    europeAmerica: SessionStats;
  };
}

interface SessionStats {
  trades: number;
  pnl: number;
  winRate: number;
  avgTrade: number;
  bestHour: string;
  worstHour: string;
}

/**
 * Analisi temporale avanzata
 */
export interface SeasonalityMetrics {
  bestHours: Array<{ hour: string; pnl: number; winRate: number; trades: number }>;
  worstHours: Array<{ hour: string; pnl: number; winRate: number; trades: number }>;
  monthlyPerformance: { [month: string]: { pnl: number; winRate: number; trades: number } };
  quarterlyPerformance: { [quarter: string]: { pnl: number; winRate: number; trades: number } };
  seasonalPatterns: {
    bestMonth: string;
    worstMonth: string;
    bestQuarter: string;
    worstQuarter: string;
  };
}

// ==================== CONTEXT ====================

/**
 * Contesto di calcolo immutabile
 */
interface CalculationContext {
  trades: StandardTrade[];
  filters: MetricsFilterOptions;
  account: TradingAccount | null;
  cacheKey: string;
  timestamp: number;
}

/**
 * Cache entry con metadata
 */
interface CacheEntry<T> {
  value: T;
  timestamp: number;
  contextKey: string;
  hits: number;
}

// ==================== ENGINE ====================

/**
 * 🎯 MOTORE PRINCIPALE
 */
export class TradingMetricsEngine {
  // Cache globale con LRU
  private static cache = new Map<string, CacheEntry<any>>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minuti
  private static readonly MAX_CACHE_SIZE = 100;

  /**
   * 🚀 Punto di ingresso principale
   */
  static calculate(
    trades: StandardTrade[],
    filters: MetricsFilterOptions = {},
    account: TradingAccount | null = null
  ): MetricsAPI {
    const perfId = PerformanceMonitor.startOperation('metrics', 'calculate', {
      tradeCount: trades.length,
      hasFilters: Object.keys(filters).length > 0,
      hasAccount: !!account
    });

    try {
      // Pulisci cache scaduta (lazy cleanup)
      if (this.cache.size > this.MAX_CACHE_SIZE) {
        this.clearExpiredCache();
      }

      // 🛡️ Defensive: alcuni trade arrivano da CSV / Supabase JSONB con `profit`
      // (e altri campi numerici) come stringhe. JavaScript `sum + "16.7"`
      // concatena invece di sommare → totale 0 dopo il round, mentre WR/PF
      // funzionano via string-to-number coercion. Normalizziamo qui una volta
      // per tutte invece di sparpagliare `Number(t.profit)` in 50 callsite.
      const normalizedTrades = trades.map((t) => normalizeTradeNumerics(t));

      const context = this.createContext(normalizedTrades, filters, account);
      const api = new MetricsAPI(context, this.cache);

      PerformanceMonitor.endOperation(perfId, true);
      return api;
    } catch (error) {
      PerformanceMonitor.endOperation(perfId, false, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  /**
   * Crea contesto di calcolo
   */
  private static createContext(
    allTrades: StandardTrade[],
    filters: MetricsFilterOptions,
    account: TradingAccount | null
  ): CalculationContext {
    const filteredTrades = this.applyFilters(allTrades, filters);
    const cacheKey = this.generateCacheKey(filteredTrades, filters, account);
    
    return {
      trades: filteredTrades,
      filters,
      account,
      cacheKey,
      timestamp: Date.now()
    };
  }

  /**
   * Applica filtri ai trade con validazione
   */
  private static applyFilters(
    trades: StandardTrade[],
    filters: MetricsFilterOptions
  ): StandardTrade[] {
    let filtered = [...trades];

    // Filtro date - usa entryTime o date come fallback (coerente con Dashboard)
    if (filters.startDate) {
      const startTime = filters.startDate.getTime();
      filtered = filtered.filter(t => {
        const tradeTime = new Date(t.date || '').getTime();
        return !isNaN(tradeTime) && tradeTime >= startTime;
      });
    }

    if (filters.endDate) {
      const endTime = filters.endDate.getTime();
      filtered = filtered.filter(t => {
        const tradeTime = new Date(t.date || '').getTime();
        return !isNaN(tradeTime) && tradeTime <= endTime;
      });
    }

    // Filtro simboli
    if (filters.symbols && filters.symbols.length > 0) {
      const symbolSet = new Set(filters.symbols);
      filtered = filtered.filter(t => symbolSet.has(t.symbol));
    }

    // Filtro direzioni
    if (filters.directions && filters.directions.length > 0) {
      const directionSet = new Set(filters.directions);
      filtered = filtered.filter(t => directionSet.has(t.type));
    }

    // Filtro profitto
    if (filters.minProfit !== undefined) {
      filtered = filtered.filter(t => t.profit >= filters.minProfit!);
    }
    if (filters.maxProfit !== undefined) {
      filtered = filtered.filter(t => t.profit <= filters.maxProfit!);
    }

    return filtered;
  }

  /**
   * Genera chiave cache univoca con hash
   */
  private static generateCacheKey(
    trades: StandardTrade[],
    filters: MetricsFilterOptions,
    account: TradingAccount | null
  ): string {
    const tradeHash = trades.length > 0 
      ? `${trades.length}-${trades[0].id}-${trades[trades.length - 1].id}`
      : 'empty';
    
    const filterHash = JSON.stringify(filters);
    const accountHash = account?.id || 'no-account';
    
    const str = `${tradeHash}:${filterHash}:${accountHash}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `metrics_${hash}`;
  }

  /**
   * Pulisce cache scaduta (LRU-like)
   */
  static clearExpiredCache(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    for (const [key, entry] of entries) {
      if (now - entry.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }

    if (this.cache.size > this.MAX_CACHE_SIZE) {
      const sorted = Array.from(this.cache.entries())
        .sort((a, b) => a[1].hits - b[1].hits);
      
      const toRemove = sorted.slice(0, Math.floor(this.MAX_CACHE_SIZE * 0.3));
      toRemove.forEach(([key]) => this.cache.delete(key));
    }

    console.log(`🧹 Cache cleaned: ${entries.length - this.cache.size} entries removed`);
  }

  /**
   * Svuota tutta la cache
   */
  static clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Cache completamente svuotata');
  }

  /**
   * Statistiche cache (per debugging)
   */
  static getCacheStats() {
    const entries = Array.from(this.cache.values());
    const totalHits = entries.reduce((sum, e) => sum + e.hits, 0);
    const avgHits = entries.length > 0 ? totalHits / entries.length : 0;
    
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      totalHits,
      avgHitsPerEntry: Math.round(avgHits * 100) / 100,
      oldestEntry: entries.length > 0 ? 
        Math.min(...entries.map(e => e.timestamp)) : null
    };
  }
}

// ==================== API ====================

/**
 * 🎨 API FLUENTE PER ACCESSO METRICHE
 */
export class MetricsAPI {
  private context: CalculationContext;
  private cache: Map<string, CacheEntry<any>>;

  constructor(context: CalculationContext, cache: Map<string, CacheEntry<any>>) {
    this.context = context;
    this.cache = cache;
  }

  // ==================== PERFORMANCE ====================

  /**
   * 📊 Metriche di performance base
   */
  get performance(): PerformanceMetrics {
    return this.getCached('performance', () => {
      const trades = this.context.trades;
      if (trades.length === 0) return this.emptyPerformance();

      const winning = trades.filter(t => t.profit > 0);
      const losing = trades.filter(t => t.profit < 0);

      const totalProfit = trades.reduce((sum, t) => sum + t.profit, 0);
      const grossProfit = winning.reduce((sum, t) => sum + t.profit, 0);
      const grossLoss = Math.abs(losing.reduce((sum, t) => sum + t.profit, 0));

      const winRate = trades.length > 0 ? (winning.length / trades.length) * 100 : 0;
      const lossRate = trades.length > 0 ? (losing.length / trades.length) * 100 : 0;
      
      const avgWin = winning.length > 0 ? grossProfit / winning.length : 0;
      const avgLoss = losing.length > 0 ? grossLoss / losing.length : 0;

      const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : 
                           grossProfit > 0 ? 999 : 0;

      return {
        totalTrades: trades.length,
        winningTrades: winning.length,
        losingTrades: losing.length,
        winRate: this.round(winRate, 1),
        lossRate: this.round(lossRate, 1),
        totalNetPnl: this.round(totalProfit, 2),
        grossProfit: this.round(grossProfit, 2),
        grossLoss: this.round(grossLoss, 2),
        averageTrade: this.round(totalProfit / trades.length, 2),
        averageWin: this.round(avgWin, 2),
        averageLoss: this.round(avgLoss, 2),
        profitFactor: this.round(profitFactor, 2),
        bestTrade: this.round(Math.max(...trades.map(t => t.profit)), 2),
        worstTrade: this.round(Math.min(...trades.map(t => t.profit)), 2),
      };
    });
  }

  // ==================== RISK ====================

  /**
   * ⚠️ Metriche di rischio
   */
  get risk(): RiskMetrics {
    return this.getCached('risk', () => {
      const trades = this.context.trades;
      if (trades.length < 2) return this.emptyRisk();

      const maxDD = this.calculateMaxDrawdown();
      const sharpe = this.calculateSharpeRatio();
      const sortino = this.calculateSortinoRatio();
      const streaks = this.calculateConsecutiveStreaks();
      const stdDev = this.calculateStandardDeviation();

      return {
        maxDrawdown: maxDD,
        sharpeRatio: sharpe,
        sortinoRatio: sortino,
        maxConsecutiveWins: streaks.maxWins,
        maxConsecutiveLosses: streaks.maxLosses,
        standardDeviation: stdDev,
        volatility: this.round(stdDev / Math.sqrt(trades.length), 2)
      };
    });
  }

  // ==================== BREAKDOWN ====================

  /**
   * 🔍 Breakdown per asset, direzione, tempo
   */
  get breakdown(): BreakdownMetrics {
    return this.getCached('breakdown', () => ({
      byAsset: this.calculateByAsset(),
      byDirection: this.calculateByDirection(),
      byHourOfDay: this.calculateByHourOfDay(),
      byDayOfWeek: this.calculateByDayOfWeek()
    }));
  }

  // ==================== ADVANCED ====================

  /**
   * 🎓 Metriche avanzate
   */
  get advanced(): AdvancedMetrics {
    return this.getCached('advanced', () => {
      const perf = this.performance;
      const risk = this.risk;

      const expectancy = this.calculateExpectancy(perf);
      const kelly = this.calculateKellyCriterion(perf);
      const sqn = this.calculateSQN();
      const g2p = this.calculateGainToPainRatio();
      const pi = perf.grossProfit / Math.abs(perf.grossLoss || 1);
      const recovery = Math.abs(perf.totalNetPnl / (risk.maxDrawdown.absolute || 1));

      return {
        expectancy: this.round(expectancy, 2),
        kellyCriterion: this.round(kelly, 3),
        sqn: this.round(sqn, 2),
        gainToPainRatio: this.round(g2p, 2),
        profitabilityIndex: this.round(pi, 2),
        recoveryFactor: this.round(recovery, 2)
      };
    });
  }

  // ==================== DURATION ====================

  /**
   * ⏱️ Analisi durata trade
   */
  get duration(): DurationMetrics {
    return this.getCached('duration', () => {
      const trades = this.context.trades.filter(t => t.durationSec && t.durationSec > 0);
      if (trades.length === 0) return this.emptyDuration();

      const winning = trades.filter(t => t.profit > 0);
      const losing = trades.filter(t => t.profit < 0);

      const avgWin = winning.length > 0 ? 
        winning.reduce((sum, t) => sum + (t.durationSec || 0), 0) / winning.length : 0;
      
      const avgLoss = losing.length > 0 ?
        losing.reduce((sum, t) => sum + (t.durationSec || 0), 0) / losing.length : 0;

      return {
        averageWinDuration: this.round(avgWin, 0),
        averageLossDuration: this.round(avgLoss, 0),
        maxDuration: Math.max(...trades.map(t => t.durationSec || 0)),
        minDuration: Math.min(...trades.map(t => t.durationSec || 0).filter(d => d > 0))
      };
    });
  }

  // ==================== EMOTIONAL METRICS ====================

  /**
   * 🧠 Metriche emotive e pattern psicologici
   */
  get emotional(): EmotionalMetrics {
    return this.getCached('emotional', () => {
      const trades = this.context.trades;
      if (trades.length < 10) return this.emptyEmotional();

      const overtradingScore = this.calculateOvertradingScore(trades);
      const revengeTrades = this.detectRevengeTrades(trades);
      const emotionalIndex = this.calculateEmotionalIndex(trades);
      const { avgPerDay, spikes } = this.calculateTradingFrequency(trades);
      const impulsivePercentage = this.calculateImpulsiveTrades(trades);

      return {
        overtradingScore: this.round(overtradingScore, 1),
        revengeTradesCount: revengeTrades.count,
        revengeTradesPnl: this.round(revengeTrades.pnl, 2),
        emotionalTradingIndex: this.round(emotionalIndex, 1),
        averageTradesPerDay: this.round(avgPerDay, 1),
        tradingFrequencySpikes: spikes,
        impulsiveTradePercentage: this.round(impulsivePercentage, 1)
      };
    });
  }

  // ==================== ADVANCED RISK ====================

  /**
   * ⚠️ Risk metrics avanzati
   */
  get advancedRisk(): AdvancedRiskMetrics {
    return this.getCached('advancedRisk', () => {
      const trades = this.context.trades;
      if (trades.length < 10) return this.emptyAdvancedRisk();

      const returns = trades.map(t => t.profit);
      
      const var95 = this.calculateVaR(returns, 0.95);
      const var99 = this.calculateVaR(returns, 0.99);
      const cvar95 = this.calculateCVaR(returns, 0.95);
      const cvar99 = this.calculateCVaR(returns, 0.99);
      const ulcer = this.calculateUlcerIndex(trades);
      const calmar = this.calculateCalmarRatio(trades);
      const recoveryTime = this.calculateMaxRecoveryTime(trades);

      return {
        calmarRatio: this.round(calmar, 2),
        valueAtRisk95: this.round(var95, 2),
        valueAtRisk99: this.round(var99, 2),
        conditionalVaR95: this.round(cvar95, 2),
        conditionalVaR99: this.round(cvar99, 2),
        ulcerIndex: this.round(ulcer, 2),
        maxTimeToRecover: recoveryTime
      };
    });
  }

  // ==================== SESSION ANALYSIS ====================

  /**
   * 🌍 Performance per sessione di trading
   */
  get sessions(): SessionMetrics {
    return this.getCached('sessions', () => {
      const trades = this.context.trades.filter(t => t.entryTime);
      if (trades.length === 0) return this.emptySessions();

      return {
        asian: this.calculateSessionStats(trades, 0, 9),
        european: this.calculateSessionStats(trades, 7, 16),
        american: this.calculateSessionStats(trades, 13, 22),
        overlap: {
          europeAsia: this.calculateSessionStats(trades, 7, 9),
          europeAmerica: this.calculateSessionStats(trades, 13, 16)
        }
      };
    });
  }

  // ==================== SEASONALITY ====================

  /**
   * 📅 Analisi stagionalità e pattern temporali
   */
  get seasonality(): SeasonalityMetrics {
    return this.getCached('seasonality', () => {
      const trades = this.context.trades;
      if (trades.length === 0) return this.emptySeasonality();

      const hourlyStats = this.calculateHourlyStats(trades);
      const sortedHours = Object.entries(hourlyStats)
        .map(([hour, stats]) => ({
          hour,
          pnl: stats.pnl,
          winRate: stats.winRate,
          trades: stats.trades
        }))
        .sort((a, b) => b.pnl - a.pnl);

      const bestHours = sortedHours.slice(0, 3);
      const worstHours = sortedHours.slice(-3).reverse();

      const monthlyPerformance = this.calculateMonthlyPerformance(trades);
      const quarterlyPerformance = this.calculateQuarterlyPerformance(trades);
      const seasonalPatterns = this.identifySeasonalPatterns(
        monthlyPerformance,
        quarterlyPerformance
      );

      return {
        bestHours,
        worstHours,
        monthlyPerformance,
        quarterlyPerformance,
        seasonalPatterns
      };
    });
  }

  // ==================== HELPERS PRIVATI ====================

  private calculateMaxDrawdown() {
    const trades = this.context.trades;
    if (trades.length < 2) return { absolute: 0, percentage: 0, duration: 0 };

    const equity: number[] = [];
    let current = (this.context.account as any)?.balance || 10000;
    equity.push(current);

    for (const trade of trades) {
      current += trade.profit;
      equity.push(current);
    }

    let maxDD = 0;
    let maxDDPercent = 0;
    let peak = equity[0];
    let drawdownStart = 0;
    let drawdownEnd = 0;
    let currentStart = 0;

    for (let i = 0; i < equity.length; i++) {
      if (equity[i] > peak) {
        peak = equity[i];
        currentStart = i;
      }

      const dd = equity[i] - peak;
      const ddPercent = peak > 0 ? (dd / peak) * 100 : 0;

      if (dd < maxDD) {
        maxDD = dd;
        maxDDPercent = ddPercent;
        drawdownStart = currentStart;
        drawdownEnd = i;
      }
    }

    const duration = drawdownEnd > drawdownStart ? 
      this.calculateDaysBetween(
        trades[drawdownStart]?.date,
        trades[Math.min(drawdownEnd, trades.length - 1)]?.date
      ) : 0;

    return {
      absolute: this.round(Math.abs(maxDD), 2),
      percentage: this.round(Math.abs(maxDDPercent), 1),
      duration
    };
  }

  private calculateSharpeRatio(): number {
    const trades = this.context.trades;
    if (trades.length < 2) return 0;

    const returns = trades.map(t => t.profit);
    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    
    const variance = returns.reduce((sum, r) => 
      sum + Math.pow(r - avgReturn, 2), 0
    ) / returns.length;
    
    const stdDev = Math.sqrt(variance);
    
    if (stdDev === 0) return 0;
    
    const sharpe = (avgReturn / stdDev) * Math.sqrt(252);
    
    return this.round(sharpe, 2);
  }

  private calculateSortinoRatio(): number {
    const trades = this.context.trades;
    if (trades.length < 2) return 0;

    const returns = trades.map(t => t.profit);
    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const negativeReturns = returns.filter(r => r < 0);
    
    if (negativeReturns.length === 0) return 0;

    const downsideVariance = negativeReturns.reduce((sum, r) => 
      sum + Math.pow(r, 2), 0
    ) / negativeReturns.length;
    
    const downsideDeviation = Math.sqrt(downsideVariance);

    if (downsideDeviation === 0) return 0;
    
    const sortino = (avgReturn / downsideDeviation) * Math.sqrt(252);
    return this.round(sortino, 2);
  }

  private calculateConsecutiveStreaks() {
    const trades = this.context.trades;
    let maxWins = 0, maxLosses = 0;
    let currentWins = 0, currentLosses = 0;

    for (const trade of trades) {
      if (trade.profit > 0) {
        currentWins++;
        currentLosses = 0;
        maxWins = Math.max(maxWins, currentWins);
      } else if (trade.profit < 0) {
        currentLosses++;
        currentWins = 0;
        maxLosses = Math.max(maxLosses, currentLosses);
      }
    }

    return { maxWins, maxLosses };
  }

  private calculateStandardDeviation(): number {
    const trades = this.context.trades;
    if (trades.length < 2) return 0;

    const mean = trades.reduce((sum, t) => sum + t.profit, 0) / trades.length;
    const variance = trades.reduce((sum, t) => 
      sum + Math.pow(t.profit - mean, 2), 0
    ) / trades.length;

    return this.round(Math.sqrt(variance), 2);
  }

  private calculateByAsset() {
    const trades = this.context.trades;
    const assetMap = new Map<string, { count: number; profit: number; wins: number }>();

    for (const trade of trades) {
      const stats = assetMap.get(trade.symbol) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      assetMap.set(trade.symbol, stats);
    }

    return Array.from(assetMap.entries())
      .map(([symbol, stats]) => ({
        symbol,
        trades: stats.count,
        profit: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1),
        avgTrade: this.round(stats.profit / stats.count, 2)
      }))
      .sort((a, b) => b.profit - a.profit);
  }

  private calculateByDirection() {
    const long = this.context.trades.filter(t => t.type === 'BUY' || t.type === 'LONG');
    const short = this.context.trades.filter(t => t.type === 'SELL' || t.type === 'SHORT');

    const calcStats = (trades: StandardTrade[]): DirectionStats => {
      if (trades.length === 0) return { trades: 0, profit: 0, winRate: 0, avgTrade: 0 };
      
      const profit = trades.reduce((sum, t) => sum + t.profit, 0);
      const wins = trades.filter(t => t.profit > 0).length;
      
      return {
        trades: trades.length,
        profit: this.round(profit, 2),
        winRate: this.round((wins / trades.length) * 100, 1),
        avgTrade: this.round(profit / trades.length, 2)
      };
    };

    return {
      long: calcStats(long),
      short: calcStats(short)
    };
  }

  private calculateByHourOfDay() {
    const trades = this.context.trades.filter(t => t.entryTime);
    const hourMap = new Map<number, { count: number; profit: number; wins: number }>();

    for (const trade of trades) {
      const hour = new Date(`${trade.date}T${trade.entryTime!}`).getHours();
      const stats = hourMap.get(hour) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      hourMap.set(hour, stats);
    }

    const result: { [hour: string]: TimeSlotStats } = {};
    for (const [hour, stats] of hourMap.entries()) {
      result[hour.toString().padStart(2, '0')] = {
        trades: stats.count,
        pnl: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1)
      };
    }

    return result;
  }

  private calculateByDayOfWeek() {
    const trades = this.context.trades;
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const dayMap = new Map<string, { count: number; profit: number; wins: number }>();

    for (const trade of trades) {
      const dayName = days[new Date(trade.date).getDay()];
      const stats = dayMap.get(dayName) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      dayMap.set(dayName, stats);
    }

    const result: { [day: string]: TimeSlotStats } = {};
    for (const [day, stats] of dayMap.entries()) {
      result[day] = {
        trades: stats.count,
        pnl: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1)
      };
    }

    return result;
  }

  private calculateExpectancy(perf: PerformanceMetrics): number {
    const winProb = perf.winRate / 100;
    const lossProb = 1 - winProb;
    
    return (winProb * perf.averageWin) - (lossProb * perf.averageLoss);
  }

  private calculateKellyCriterion(perf: PerformanceMetrics): number {
    const winProb = perf.winRate / 100;
    const lossProb = 1 - winProb;
    
    if (lossProb === 0 || perf.averageLoss === 0) return 0;
    
    const winLossRatio = perf.averageWin / perf.averageLoss;
    const kelly = (winProb / lossProb) - (1 / winLossRatio);
    
    return Math.max(0, kelly);
  }

  private calculateSQN(): number {
    const trades = this.context.trades;
    if (trades.length < 2) return 0;

    const avgProfit = trades.reduce((sum, t) => sum + t.profit, 0) / trades.length;
    const stdDev = this.calculateStandardDeviation();

    if (stdDev === 0) return 0;

    return (avgProfit / stdDev) * Math.sqrt(trades.length);
  }

  private calculateGainToPainRatio(): number {
    const trades = this.context.trades;
    if (trades.length === 0) return 0;

    const totalGain = trades.reduce((sum, t) => sum + Math.max(0, t.profit), 0);
    const totalPain = Math.abs(trades.reduce((sum, t) => sum + Math.min(0, t.profit), 0));

    if (totalPain === 0) return totalGain > 0 ? 999 : 0;

    return totalGain / totalPain;
  }

  // ==================== EMOTIONAL CALCULATIONS ====================

  private calculateOvertradingScore(trades: StandardTrade[]): number {
    let score = 0;

    const { avgPerDay, spikes } = this.calculateTradingFrequency(trades);
    const frequencyScore = Math.min(40, (spikes / trades.length) * 100 * 2);
    score += frequencyScore;

    let rapidTrades = 0;
    for (let i = 1; i < trades.length; i++) {
      const prevTime = new Date(`${trades[i-1].date} ${trades[i-1].exitTime || '00:00'}`);
      const currTime = new Date(`${trades[i].date} ${trades[i].entryTime || '00:00'}`);
      const minutesDiff = (currTime.getTime() - prevTime.getTime()) / (1000 * 60);
      
      if (minutesDiff < 15) rapidTrades++;
    }
    const rapidScore = Math.min(30, (rapidTrades / trades.length) * 100);
    score += rapidScore;

    const perf = this.performance;
    if (avgPerDay > 5 && perf.winRate < 45) {
      score += 30;
    } else if (avgPerDay > 3 && perf.winRate < 50) {
      score += 15;
    }

    return Math.min(100, score);
  }

  private detectRevengeTrades(trades: StandardTrade[]) {
    let count = 0;
    let totalPnl = 0;

    for (let i = 1; i < trades.length; i++) {
      const prevTrade = trades[i - 1];
      const currTrade = trades[i];

      const isPrevLoss = prevTrade.profit < 0;
      const isSizeIncreased = (currTrade.quantity || 0) > (prevTrade.quantity || 0) * 1.3;
      const isSameAsset = currTrade.symbol === prevTrade.symbol;
      
      const prevTime = new Date(`${prevTrade.date} ${prevTrade.exitTime || '00:00'}`);
      const currTime = new Date(`${currTrade.date} ${currTrade.entryTime || '00:00'}`);
      const minutesDiff = (currTime.getTime() - prevTime.getTime()) / (1000 * 60);
      const isQuick = minutesDiff < 30 && minutesDiff > 0;

      if (isPrevLoss && (isSizeIncreased || (isSameAsset && isQuick))) {
        count++;
        totalPnl += currTrade.profit;
      }
    }

    return { count, pnl: totalPnl };
  }

  private calculateEmotionalIndex(trades: StandardTrade[]): number {
    let emotionalScore = 0;

    const sizes = trades.map(t => t.quantity || 1);
    const avgSize = sizes.reduce((sum, s) => sum + s, 0) / sizes.length;
    const variance = sizes.reduce((sum, s) => sum + Math.pow(s - avgSize, 2), 0) / sizes.length;
    const cv = Math.sqrt(variance) / avgSize;
    emotionalScore += Math.min(3, cv * 3);

    let lossStreakTrades = 0;
    let consecutiveLosses = 0;
    for (const trade of trades) {
      if (trade.profit < 0) {
        consecutiveLosses++;
      } else {
        if (consecutiveLosses >= 2) lossStreakTrades++;
        consecutiveLosses = 0;
      }
    }
    emotionalScore += Math.min(4, (lossStreakTrades / trades.length) * 20);

    const hourCounts = new Map<number, number>();
    for (const trade of trades.filter(t => t.entryTime)) {
      const hour = parseInt(trade.entryTime!.split(':')[0]);
      hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
    }
    const hourVariance = Array.from(hourCounts.values()).reduce((sum, count) => {
      const avg = trades.length / 24;
      return sum + Math.pow(count - avg, 2);
    }, 0) / Math.max(1, hourCounts.size);
    emotionalScore += Math.min(3, Math.sqrt(hourVariance) / 10);

    return Math.min(10, emotionalScore);
  }

  private calculateTradingFrequency(trades: StandardTrade[]) {
    if (trades.length < 2) return { avgPerDay: 0, spikes: 0 };

    const tradesByDay = new Map<string, number>();
    for (const trade of trades) {
      const day = trade.date.split('T')[0];
      tradesByDay.set(day, (tradesByDay.get(day) || 0) + 1);
    }

    const dailyCounts = Array.from(tradesByDay.values());
    const avgPerDay = dailyCounts.reduce((sum, c) => sum + c, 0) / dailyCounts.length;
    
    const spikes = dailyCounts.filter(c => c > avgPerDay * 2).length;

    return { avgPerDay, spikes };
  }

  private calculateImpulsiveTrades(trades: StandardTrade[]): number {
    if (trades.length < 2) return 0;

    let impulsiveCount = 0;
    for (let i = 1; i < trades.length; i++) {
      const prevTime = new Date(`${trades[i-1].date} ${trades[i-1].exitTime || '00:00'}`);
      const currTime = new Date(`${trades[i].date} ${trades[i].entryTime || '00:00'}`);
      const minutesDiff = (currTime.getTime() - prevTime.getTime()) / (1000 * 60);
      
      if (minutesDiff < 5 && minutesDiff > 0) {
        impulsiveCount++;
      }
    }

    return (impulsiveCount / (trades.length - 1)) * 100;
  }

  // ==================== ADVANCED RISK CALCULATIONS ====================

  private calculateVaR(returns: number[], confidence: number): number {
    if (returns.length === 0) return 0;

    const sorted = [...returns].sort((a, b) => a - b);
    const index = Math.floor((1 - confidence) * sorted.length);
    
    return Math.abs(sorted[index] || 0);
  }

  private calculateCVaR(returns: number[], confidence: number): number {
    if (returns.length === 0) return 0;

    const sorted = [...returns].sort((a, b) => a - b);
    const varIndex = Math.floor((1 - confidence) * sorted.length);
    
    const tailReturns = sorted.slice(0, varIndex);
    if (tailReturns.length === 0) return 0;
    
    const cvar = Math.abs(
      tailReturns.reduce((sum, r) => sum + r, 0) / tailReturns.length
    );
    
    return cvar;
  }

  private calculateUlcerIndex(trades: StandardTrade[]): number {
    if (trades.length < 2) return 0;

    const equity: number[] = [];
    let current = (this.context.account as any)?.balance || 10000;
    equity.push(current);

    for (const trade of trades) {
      current += trade.profit;
      equity.push(current);
    }

    const drawdowns: number[] = [];
    let peak = equity[0];

    for (const value of equity) {
      if (value > peak) peak = value;
      
      const drawdown = peak > 0 ? ((value - peak) / peak) * 100 : 0;
      drawdowns.push(drawdown * drawdown);
    }

    const meanSquaredDD = drawdowns.reduce((sum, dd) => sum + dd, 0) / drawdowns.length;
    return Math.sqrt(meanSquaredDD);
  }

  private calculateCalmarRatio(trades: StandardTrade[]): number {
    if (trades.length < 2) return 0;

    const perf = this.performance;
    const maxDD = this.calculateMaxDrawdown();

    if (maxDD.percentage === 0) return 0;

    const days = this.calculateDaysBetween(trades[0].date, trades[trades.length - 1].date);
    const years = days / 365.25;

    if (years === 0) return 0;

    const totalReturn = perf.totalNetPnl / ((this.context.account as any)?.balance || 10000);
    const annualizedReturn = (Math.pow(1 + totalReturn, 1 / years) - 1) * 100;

    return annualizedReturn / maxDD.percentage;
  }

  private calculateMaxRecoveryTime(trades: StandardTrade[]): number {
    if (trades.length < 2) return 0;

    const equity: number[] = [];
    let current = (this.context.account as any)?.balance || 10000;
    
    for (const trade of trades) {
      current += trade.profit;
      equity.push(current);
    }

    let maxRecovery = 0;
    let peak = equity[0];
    let drawdownStart = 0;

    for (let i = 0; i < equity.length; i++) {
      if (equity[i] >= peak) {
        if (equity[i] > peak && drawdownStart < i) {
          const recoveryTime = this.calculateDaysBetween(
            trades[drawdownStart].date,
            trades[i].date
          );
          maxRecovery = Math.max(maxRecovery, recoveryTime);
        }
        peak = equity[i];
        drawdownStart = i;
      }
    }

    return maxRecovery;
  }

  // ==================== SESSION CALCULATIONS ====================

  private calculateSessionStats(
    trades: StandardTrade[],
    startHour: number,
    endHour: number
  ): SessionStats {
    const sessionTrades = trades.filter(t => {
      if (!t.entryTime) return false;
      const hour = parseInt(t.entryTime.split(':')[0]);
      return hour >= startHour && hour < endHour;
    });

    if (sessionTrades.length === 0) {
      return {
        trades: 0, pnl: 0, winRate: 0, avgTrade: 0,
        bestHour: 'N/A', worstHour: 'N/A'
      };
    }

    const pnl = sessionTrades.reduce((sum, t) => sum + t.profit, 0);
    const wins = sessionTrades.filter(t => t.profit > 0).length;
    const winRate = (wins / sessionTrades.length) * 100;

    const hourlyStats = new Map<number, number>();
    for (const trade of sessionTrades) {
      const hour = parseInt(trade.entryTime!.split(':')[0]);
      hourlyStats.set(hour, (hourlyStats.get(hour) || 0) + trade.profit);
    }

    const sortedHours = Array.from(hourlyStats.entries())
      .sort((a, b) => b[1] - a[1]);
    
    const bestHour = sortedHours.length > 0 ? 
      `${sortedHours[0][0].toString().padStart(2, '0')}:00` : 'N/A';
    const worstHour = sortedHours.length > 0 ? 
      `${sortedHours[sortedHours.length - 1][0].toString().padStart(2, '0')}:00` : 'N/A';

    return {
      trades: sessionTrades.length,
      pnl: this.round(pnl, 2),
      winRate: this.round(winRate, 1),
      avgTrade: this.round(pnl / sessionTrades.length, 2),
      bestHour,
      worstHour
    };
  }

  // ==================== SEASONALITY CALCULATIONS ====================

  private calculateHourlyStats(trades: StandardTrade[]) {
    const hourMap = new Map<string, { count: number; profit: number; wins: number }>();

    for (const trade of trades.filter(t => t.entryTime)) {
      const hour = trade.entryTime!.split(':')[0].padStart(2, '0');
      const stats = hourMap.get(hour) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      hourMap.set(hour, stats);
    }

    const result: { [hour: string]: any } = {};
    for (const [hour, stats] of hourMap.entries()) {
      result[hour] = {
        trades: stats.count,
        pnl: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1)
      };
    }

    return result;
  }

  private calculateMonthlyPerformance(trades: StandardTrade[]) {
    const months = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];

    const monthMap = new Map<string, { count: number; profit: number; wins: number }>();

    for (const trade of trades) {
      const date = new Date(trade.date);
      const monthName = months[date.getMonth()];
      const stats = monthMap.get(monthName) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      monthMap.set(monthName, stats);
    }

    const result: { [month: string]: any } = {};
    for (const [month, stats] of monthMap.entries()) {
      result[month] = {
        trades: stats.count,
        pnl: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1)
      };
    }

    return result;
  }

  private calculateQuarterlyPerformance(trades: StandardTrade[]) {
    const quarterMap = new Map<string, { count: number; profit: number; wins: number }>();

    for (const trade of trades) {
      const date = new Date(trade.date);
      const quarter = `Q${Math.floor(date.getMonth() / 3) + 1}`;
      const stats = quarterMap.get(quarter) || { count: 0, profit: 0, wins: 0 };
      
      stats.count++;
      stats.profit += trade.profit;
      if (trade.profit > 0) stats.wins++;
      
      quarterMap.set(quarter, stats);
    }

    const result: { [quarter: string]: any } = {};
    for (const [quarter, stats] of quarterMap.entries()) {
      result[quarter] = {
        trades: stats.count,
        pnl: this.round(stats.profit, 2),
        winRate: this.round((stats.wins / stats.count) * 100, 1)
      };
    }

    return result;
  }

  private identifySeasonalPatterns(monthly: any, quarterly: any) {
    const monthEntries = Object.entries(monthly);
    const sortedMonths = monthEntries.sort((a: any, b: any) => b[1].pnl - a[1].pnl);
    
    const bestMonth = sortedMonths.length > 0 ? sortedMonths[0][0] : 'N/A';
    const worstMonth = sortedMonths.length > 0 ? 
      sortedMonths[sortedMonths.length - 1][0] : 'N/A';

    const quarterEntries = Object.entries(quarterly);
    const sortedQuarters = quarterEntries.sort((a: any, b: any) => b[1].pnl - a[1].pnl);
    
    const bestQuarter = sortedQuarters.length > 0 ? sortedQuarters[0][0] : 'N/A';
    const worstQuarter = sortedQuarters.length > 0 ? 
      sortedQuarters[sortedQuarters.length - 1][0] : 'N/A';

    return {
      bestMonth,
      worstMonth,
      bestQuarter,
      worstQuarter
    };
  }

  // ==================== UTILITY ====================

  private getCached<T>(key: string, calculator: () => T): T {
    const fullKey = `${this.context.cacheKey}_${key}`;
    const cached = this.cache.get(fullKey);

    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      cached.hits++;
      PerformanceMonitor.recordCacheHit();
      return cached.value as T;
    }

    PerformanceMonitor.recordCacheMiss();
    const perfId = PerformanceMonitor.startOperation('cache', `calculate_${key}`, {
      cacheKey: this.context.cacheKey
    });

    try {
      const value = calculator();
      this.cache.set(fullKey, {
        value,
        timestamp: Date.now(),
        contextKey: this.context.cacheKey,
        hits: 1
      });

      PerformanceMonitor.endOperation(perfId, true);
      return value;
    } catch (error) {
      PerformanceMonitor.endOperation(perfId, false, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  private round(value: number, decimals: number): number {
    if (!isFinite(value)) return 0;
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  private calculateDaysBetween(date1?: string, date2?: string): number {
    if (!date1 || !date2) return 0;
    const d1 = new Date(date1).getTime();
    const d2 = new Date(date2).getTime();
    if (isNaN(d1) || isNaN(d2)) return 0;
    return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
  }

  // ==================== EMPTY STATES ====================

  private emptyPerformance(): PerformanceMetrics {
    return {
      totalTrades: 0, winningTrades: 0, losingTrades: 0, 
      winRate: 0, lossRate: 0, totalNetPnl: 0, grossProfit: 0, 
      grossLoss: 0, averageTrade: 0, averageWin: 0, averageLoss: 0,
      profitFactor: 0, bestTrade: 0, worstTrade: 0
    };
  }

  private emptyRisk(): RiskMetrics {
    return {
      maxDrawdown: { absolute: 0, percentage: 0, duration: 0 },
      sharpeRatio: 0, sortinoRatio: 0,
      maxConsecutiveWins: 0, maxConsecutiveLosses: 0,
      standardDeviation: 0, volatility: 0
    };
  }

  private emptyDuration(): DurationMetrics {
    return {
      averageWinDuration: 0, averageLossDuration: 0,
      maxDuration: 0, minDuration: 0
    };
  }

  private emptyEmotional(): EmotionalMetrics {
    return {
      overtradingScore: 0,
      revengeTradesCount: 0,
      revengeTradesPnl: 0,
      emotionalTradingIndex: 0,
      averageTradesPerDay: 0,
      tradingFrequencySpikes: 0,
      impulsiveTradePercentage: 0
    };
  }

  private emptyAdvancedRisk(): AdvancedRiskMetrics {
    return {
      calmarRatio: 0,
      valueAtRisk95: 0,
      valueAtRisk99: 0,
      conditionalVaR95: 0,
      conditionalVaR99: 0,
      ulcerIndex: 0,
      maxTimeToRecover: 0
    };
  }

  private emptySessions(): SessionMetrics {
    const emptySession: SessionStats = {
      trades: 0, pnl: 0, winRate: 0, avgTrade: 0,
      bestHour: 'N/A', worstHour: 'N/A'
    };

    return {
      asian: emptySession,
      european: emptySession,
      american: emptySession,
      overlap: {
        europeAsia: emptySession,
        europeAmerica: emptySession
      }
    };
  }

  private emptySeasonality(): SeasonalityMetrics {
    return {
      bestHours: [],
      worstHours: [],
      monthlyPerformance: {},
      quarterlyPerformance: {},
      seasonalPatterns: {
        bestMonth: 'N/A',
        worstMonth: 'N/A',
        bestQuarter: 'N/A',
        worstQuarter: 'N/A'
      }
    };
  }

  /**
   * 📤 Export tutte le metriche
   */
  toJSON() {
    return {
      performance: this.performance,
      risk: this.risk,
      breakdown: this.breakdown,
      advanced: this.advanced,
      duration: this.duration,
      emotional: this.emotional,
      advancedRisk: this.advancedRisk,
      sessions: this.sessions,
      seasonality: this.seasonality,
      _meta: {
        tradesCount: this.context.trades.length,
        filtersApplied: Object.keys(this.context.filters).length > 0,
        cacheKey: this.context.cacheKey,
        timestamp: this.context.timestamp
      }
    };
  }
}

// ==================== EXPORTS ====================

export default TradingMetricsEngine;
